import React, { useState } from 'react';
import { GameMode, Difficulty, GameType } from '../types/game';
import { Match3Mode } from '../utils/match3';

interface ModeSelectProps {
  onModeSelect: (mode: GameMode, difficulty?: Difficulty, gameType?: GameType, match3Mode?: Match3Mode, playerColor?: 'black' | 'white') => void;
  selectedGameType?: GameType | null;
  onBackToGameTypeSelect?: () => void;
}

const ModeSelect: React.FC<ModeSelectProps> = ({ onModeSelect, selectedGameType: propSelectedGameType, onBackToGameTypeSelect }) => {
  const [internalSelectedGameType, setInternalSelectedGameType] = useState<GameType | null>(null);
  const [selectedColor, setSelectedColor] = useState<'black' | 'white'>('black'); // 直棋人机对战的颜色选择

  // 使用传入的 selectedGameType 优先，否则使用内部状态
  const selectedGameType = propSelectedGameType || internalSelectedGameType;

  const handleGameTypeSelect = (gameType: GameType) => {
    setInternalSelectedGameType(gameType);
  };

  const getGameTypeIcon = (type: GameType) => {
    switch (type) {
      case 'gomoku': return '⚫';
      case 'reversi': return '⚪';
      case 'checkers': return '♟';
      case 'jungle': return '🐾';
      case 'xiangqi': return '🐴';
      case 'tigerchicken': return '🐅';
      case 'tictactoe': return '⭕';
      case 'match3': return '💎';
      case 'connectfour': return '🔵';
      case 'hnefatafl': return '🛡️';
      case 'straight': return '⚡';
      case 'tablut': return '👑';
      default: return '🎮';
    }
  };

  const getGameTypeName = (type: GameType) => {
    switch (type) {
      case 'gomoku': return '五子棋';
      case 'reversi': return '黑白棋';
      case 'checkers': return '国际跳棋';
      case 'jungle': return '斗兽棋';
      case 'xiangqi': return '中国象棋';
      case 'tigerchicken': return '虎鸡虫棒棋';
      case 'tictactoe': return '井字棋';
      case 'match3': return '消消看';
      case 'connectfour': return '四子棋';
      case 'hnefatafl': return '维京板棋';
      case 'straight': return '直棋';
      case 'tablut': return '萨米板棋';
      default: return '未知游戏';
    }
  };

  const getGameTypeDescription = (type: GameType) => {
    switch (type) {
      case 'gomoku':
        return '以横、竖或斜线连成五颗棋子为获胜条件的棋类游戏';
      case 'reversi':
        return '又称奥赛罗棋、正反棋，翻转棋子，占领棋盘的策略游戏';
      case 'checkers':
        return '斜向移动，强制吃子，成王升级的国际跳棋';
      case 'jungle':
        return '又称动物棋，动物格斗，吃子推进，占领巢穴的斗兽棋';
      case 'xiangqi':
        return '楚河汉界，将帅对决，经典国粹，传统中国象棋';
      case 'tigerchicken':
        return '虎吃鸡，鸡啄虫，虫蛀棒，棒打虎的相克游戏';
      case 'tictactoe':
        return '经典三连棋游戏，支持多种棋盘大小和连线规则';
      case 'match3':
        return '点击消除相连的同色宝石，挑战高分的休闲益智游戏';
      case 'connectfour':
        return '垂直落子，四子连线获胜，策略性强的连珠游戏';
      case 'hnefatafl':
        return '维京板棋，非对称对抗，国王逃脱vs围堵';
      case 'straight':
        return '三同心方格对角线，三子成直吃子，传统直棋游戏';
      case 'tablut':
        return '北欧传统棋类，9×9棋盘，护王方国王与守卫vs攻击者';
      default:
        return '精彩的棋类游戏';
    }
  };

  if (selectedGameType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
        {/* 主要内容 */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl w-full">
            {/* 返回按钮 */}
            <button
              onClick={() => {
                // 直接返回到首页，不经过游戏选择页面
                setInternalSelectedGameType(null);
                if (onBackToGameTypeSelect) {
                  onBackToGameTypeSelect();
                }
              }}
              className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <span className="mr-2">←</span>
              <span>返回首页</span>
            </button>

            <h1 className="text-4xl font-bold text-center mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {getGameTypeName(selectedGameType)}
            </h1>

            <div className="space-y-6">
              {/* 游戏模式选择 */}
              <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-800 mb-6">选择游戏模式</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedGameType === 'match3' ? (
                    <>
                      {/* 经典宝石模式 */}
                      <button
                        onClick={() => onModeSelect('pvp', undefined, selectedGameType, 'classic')}
                        className="group relative bg-gradient-to-br from-purple-500 to-pink-600 text-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                      >
                        <div className="text-center">
                          <div className="text-6xl mb-4">💎</div>
                          <h3 className="text-2xl font-bold mb-2">经典宝石模式</h3>
                          <p className="text-purple-100">消除彩色宝石，关卡挑战</p>
                        </div>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                      </button>

                      {/* 星星消除模式 */}
                      <button
                        onClick={() => onModeSelect('pvc', 'easy', selectedGameType, 'stars')}
                        className="group relative bg-gradient-to-br from-yellow-400 to-orange-500 text-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                      >
                        <div className="text-center">
                          <div className="text-6xl mb-4">⭐</div>
                          <h3 className="text-2xl font-bold mb-2">星星消除模式</h3>
                          <p className="text-yellow-100">消除所有星星，高分挑战</p>
                        </div>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                      </button>

                      {/* 开心消消乐模式 */}
                      <button
                        onClick={() => onModeSelect('pvc', 'medium', selectedGameType, 'happy')}
                        className="group relative bg-gradient-to-br from-green-400 to-blue-500 text-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                      >
                        <div className="text-center">
                          <div className="text-5xl mb-4">🐰🐻🐸</div>
                          <h3 className="text-2xl font-bold mb-2">开心消消乐模式</h3>
                          <p className="text-green-100">交换小动物，三连消除</p>
                        </div>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                      </button>

                      {/* 水果消消乐模式 */}
                      <button
                        onClick={() => onModeSelect('pvc', 'easy', selectedGameType, 'fruit')}
                        className="group relative bg-gradient-to-br from-pink-400 to-rose-500 text-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                      >
                        <div className="text-center">
                          <div className="text-5xl mb-4">🍎🍌🍓</div>
                          <h3 className="text-2xl font-bold mb-2">水果消消乐模式</h3>
                          <p className="text-pink-100">可爱水果，三连消除</p>
                        </div>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                      </button>
                    </>
                  ) : (
                    <>
                      {/* 双人对战 */}
                      <button
                        onClick={() => onModeSelect('pvp', undefined, selectedGameType)}
                        className="group relative bg-gradient-to-br from-blue-500 to-blue-600 text-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                      >
                        <div className="text-center">
                          <div className="text-6xl mb-4">👥</div>
                          <h3 className="text-2xl font-bold mb-2">双人对战</h3>
                          <p className="text-blue-100">两位玩家在同一设备上对战</p>
                        </div>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                      </button>

                      {/* 人机对战 */}
                      <button
                        onClick={() => onModeSelect('pvc', 'medium', selectedGameType, undefined, selectedGameType === 'straight' ? selectedColor : undefined)}
                        className="group relative bg-gradient-to-br from-purple-500 to-purple-600 text-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                      >
                        <div className="text-center">
                          <div className="text-6xl mb-4">🤖</div>
                          <h3 className="text-2xl font-bold mb-2">人机对战</h3>
                          <p className="text-purple-100">与AI对手对战</p>
                        </div>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* 人机对战难度选择 */}
              {selectedGameType !== 'match3' && (
                <div className="border-t pt-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">AI难度选择</h3>

                <div className="grid grid-cols-3 gap-4">
                  {/* 简单 */}
                  <button
                    onClick={() => onModeSelect('pvc', 'easy', selectedGameType, undefined, selectedGameType === 'straight' ? selectedColor : undefined)}
                    className="group bg-gradient-to-br from-green-400 to-green-500 text-white p-6 rounded-lg shadow hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                  >
                    <div className="text-center">
                      <div className="text-4xl mb-2">😊</div>
                      <h4 className="text-lg font-bold">简单</h4>
                      <p className="text-sm text-green-100 mt-1">适合初学者</p>
                    </div>
                  </button>

                  {/* 中等 */}
                  <button
                    onClick={() => onModeSelect('pvc', 'medium', selectedGameType, undefined, selectedGameType === 'straight' ? selectedColor : undefined)}
                    className="group bg-gradient-to-br from-yellow-400 to-orange-500 text-white p-6 rounded-lg shadow hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                  >
                    <div className="text-center">
                      <div className="text-4xl mb-2">🎯</div>
                      <h4 className="text-lg font-bold">中等</h4>
                      <p className="text-sm text-yellow-100 mt-1">适中的挑战</p>
                    </div>
                  </button>

                  {/* 困难 */}
                  <button
                    onClick={() => onModeSelect('pvc', 'hard', selectedGameType, undefined, selectedGameType === 'straight' ? selectedColor : undefined)}
                    className="group bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-lg shadow hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                  >
                    <div className="text-center">
                      <div className="text-4xl mb-2">🔥</div>
                      <h4 className="text-lg font-bold">困难</h4>
                      <p className="text-sm text-red-100 mt-1">高难度挑战</p>
                    </div>
                  </button>
                </div>
                </div>
              )}

              {/* 直棋颜色选择 - 仅在直棋人机对战中显示 */}
              {selectedGameType === 'straight' && (
                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">选择你的棋子颜色</h3>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* 黑棋选择 */}
                    <button
                      onClick={() => setSelectedColor('black')}
                      className={`group p-6 rounded-lg border-2 transition-all duration-300 ${
                        selectedColor === 'black'
                          ? 'border-gray-800 bg-gray-100 shadow-lg scale-105'
                          : 'border-gray-300 bg-white hover:border-gray-500 hover:shadow-md'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-4xl mb-2">⚫</div>
                        <h4 className="text-lg font-bold text-gray-800">黑棋</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {selectedColor === 'black' ? '你将使用黑棋' : '点击选择黑棋'}
                        </p>
                        {selectedColor === 'black' && (
                          <div className="mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            已选择
                          </div>
                        )}
                      </div>
                    </button>

                    {/* 白棋选择 */}
                    <button
                      onClick={() => setSelectedColor('white')}
                      className={`group p-6 rounded-lg border-2 transition-all duration-300 ${
                        selectedColor === 'white'
                          ? 'border-gray-800 bg-gray-100 shadow-lg scale-105'
                          : 'border-gray-300 bg-white hover:border-gray-500 hover:shadow-md'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-4xl mb-2">⚪</div>
                        <h4 className="text-lg font-bold text-gray-800">白棋</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {selectedColor === 'white' ? '你将使用白棋' : '点击选择白棋'}
                        </p>
                        {selectedColor === 'white' && (
                          <div className="mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            已选择
                          </div>
                        )}
                      </div>
                    </button>
                  </div>

                  <div className="text-center text-sm text-gray-600">
                    <p>💡 选择你想要使用的棋子颜色，AI将使用另一种颜色</p>
                  </div>
                </div>
              )}

              {/* 游戏说明 */}
              <div className="border-t pt-6">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">游戏特色</h3>
                  {selectedGameType === 'gomoku' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 传统五子棋规则，五子连线获胜</li>
                      <li>• 35种独特技能：传统成语 + 现代网络梗</li>
                      <li>• 能量系统和技能冷却策略</li>
                      <li>• 支持悔棋和重新开始</li>
                    </ul>
                  ) : selectedGameType === 'reversi' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 经典黑白棋规则，翻转对手棋子</li>
                      <li>• 8×8棋盘，策略性极强的游戏</li>
                      <li>• 占领更多棋子即可获胜</li>
                      <li>• 支持悔棋和重新开始</li>
                    </ul>
                  ) : selectedGameType === 'checkers' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 国际跳棋规则，斜向移动吃子</li>
                      <li>• 10×10棋盘，强制吃子规则</li>
                      <li>• 升级为王棋，增强移动能力</li>
                      <li>• 支持悔棋和重新开始</li>
                    </ul>
                  ) : selectedGameType === 'jungle' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 斗兽棋规则，8种动物各有等级</li>
                      <li>• 7×9棋盘，包含河流、陷阱、巢穴</li>
                      <li>• 大吃小规则，特殊地形互动</li>
                      <li>• 占领敌方巢穴或吃光所有棋子获胜</li>
                    </ul>
                  ) : selectedGameType === 'xiangqi' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 传统中国象棋规则，楚河汉界分明</li>
                      <li>• 9×10棋盘，七种棋子各具特色</li>
                      <li>• 将帅不能照面，马有蹩腿，象有塞眼</li>
                      <li>• 将死对方将帅即可获胜</li>
                    </ul>
                  ) : selectedGameType === 'tigerchicken' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 虎鸡虫棒相克规则：虎吃鸡，鸡啄虫，虫蛀棒，棒打虎</li>
                      <li>• 7×7棋盘，棋子只能一步一步走</li>
                      <li>• 黑方棋子可使用6条对角线，白方棋子只能使用2条主对角线</li>
                      <li>• 占领对方大本营（家）即可获胜</li>
                      <li>• 不能进入自己的大本营，只能占领对方大本营</li>
                      <li>• 非对称移动规则增加策略深度和游戏平衡性</li>
                    </ul>
                  ) : selectedGameType === 'tictactoe' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 经典井字棋游戏，规则简单易懂</li>
                      <li>• 支持三种棋盘大小：3×3、5×5、7×7</li>
                      <li>• 3×3棋盘三子连线获胜，5×5和7×7棋盘四子连线获胜</li>
                      <li>• 可在横、竖、斜四个方向形成连线</li>
                      <li>• 支持双人对战和人机对战，多种AI难度</li>
                      <li>• 游戏节奏快，适合休闲娱乐</li>
                    </ul>
                  ) : selectedGameType === 'match3' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 经典宝石模式：消除彩色宝石，达到目标分数进入下一关</li>
                      <li>• 星星消除模式：消除所有星星获得高分，空列时星星向左移动</li>
                      <li>• 开心消消乐模式：交换相邻小动物，三连直线消除</li>
                      <li>• 水果消消乐模式：可爱水果风格，三连消除，简单易上手</li>
                      <li>• 10×10棋盘，经典模式3个消除，星星模式2个消除，其他模式三连消除</li>
                      <li>• 连击系统：一次性消除越多，得分倍数越高</li>
                      <li>• 精美的视觉效果和流畅的消除动画</li>
                    </ul>
                  ) : selectedGameType === 'connectfour' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 8×8垂直棋盘，棋子从底部向上填充</li>
                      <li>• 四子连线即可获胜（横、竖、斜四个方向）</li>
                      <li>• 垂直落子机制，增加策略深度和预判要求</li>
                      <li>• 支持双人对战和人机对战，多种AI难度</li>
                      <li>• 规则简单易懂，但充满策略性</li>
                      <li>• 支持悔棋和重新开始功能</li>
                    </ul>
                  ) : selectedGameType === 'hnefatafl' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 维京时代的经典非对称对抗棋类游戏</li>
                      <li>• 11×11棋盘，进攻方24枚兵卒vs防守方13枚棋子（1国王+12兵卒）</li>
                      <li>• 防守方胜利条件：国王成功逃脱到任一角落</li>
                      <li>• 进攻方胜利条件：围堵国王使其无法移动或直接捕获</li>
                      <li>• 所有棋子纵横移动任意格数，不可斜向移动</li>
                      <li>• 夹吃规则：两枚己方棋子夹住敌方棋子时吃掉</li>
                      <li>• 特殊规则：中心格和四角为禁止停留位置（国王可停在角落）</li>
                      <li>• 国王需要被四面包围才能被捕获，体现王者尊严</li>
                    </ul>
                  ) : selectedGameType === 'straight' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 传统直棋游戏，3个同心正方形+4条对角线形成24个交叉点</li>
                      <li>• 布子阶段：双方轮流放置棋子，每人9枚，共18枚棋子</li>
                      <li>• 成直规则：三子同一直线（横、竖、斜线）形成"成直"，可立即吃掉对方1枚棋子</li>
                      <li>• 吃子限制：优先吃不在成直中的棋子，只有当对方所有棋子都在成直中才能任意吃子</li>
                      <li>• 移动阶段：棋子只能沿线移动到相邻空位，成直仍可吃子</li>
                      <li>• 飞行阶段：当一方剩余3枚棋子时，该方棋子可飞到任意空位</li>
                      <li>• 胜利条件：使对方剩余≤2枚棋子，或对方无法移动时获胜</li>
                      <li>• 策略要点：占据关键位置，防止对手成直，适时形成自己的成直获取优势</li>
                    </ul>
                  ) : selectedGameType === 'tablut' ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 北欧萨米地区的传统板棋游戏，类似维京板棋但规则更简化</li>
                      <li>• 9×9棋盘，护王方：1国王+8守卫（形成十字形）vs攻击方16名攻击者</li>
                      <li>• 护王方胜利条件：国王成功逃脱到任一角落位置</li>
                      <li>• 攻击方胜利条件：将国王四面围住使其无法移动</li>
                      <li>• 移动规则：所有棋子只能横向或纵向移动，不能斜向移动</li>
                      <li>• 夹吃规则：当两枚己方棋子夹住敌方棋子时，可吃掉该棋子</li>
                      <li>• 特殊规则：国王需要被四面包围才能被捕获，普通棋子被两面夹住即可被吃</li>
                      <li>• 策略要点：护王方要保护国王并开辟逃脱路线，攻击方要围堵国王并切断支援</li>
                    </ul>
                  ) : (
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• 精彩的棋类游戏体验</li>
                      <li>• 支持双人对战和人机对战</li>
                      <li>• 多种难度等级可选</li>
                      <li>• 支持悔棋和重新开始</li>
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-6 mt-8">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
                <div className="text-sm text-gray-300">
                  © 2025 <a href="https://oho.cn" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">oho.cn</a> 版权所有
                </div>
                <div className="text-sm text-gray-300">
                  <a href="http://beian.miit.gov.cn" target="_blank" rel="nofollow" className="hover:text-white transition-colors">浙ICP备13034790号-1</a>
                </div>
              </div>
              <div className="text-sm text-gray-400">
                益智棋类游戏合集 • 致敬经典策略游戏
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
      {/* 主要内容 */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-4 max-w-4xl w-full">
          <h1 className="text-4xl font-bold text-center mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            OHO 经典益智休闲棋类游戏合集
          </h1>

          <div className="space-y-8">
            {/* 游戏类型选择 */}
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-800 mb-8">请选择游戏类型</h2>

              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 auto-rows-fr">
                {/* 五子棋 */}
                <button
                  onClick={() => handleGameTypeSelect('gomoku')}
                  className="group relative bg-gradient-to-br from-gray-700 to-gray-900 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">⚫</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">五子棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-gray-300 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('gomoku')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-blue-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">15×15</span>
                      <span className="bg-purple-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">技能</span>
                      <span className="bg-green-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">连线</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 国际跳棋 */}
                <button
                  onClick={() => handleGameTypeSelect('checkers')}
                  className="group relative bg-gradient-to-br from-amber-600 to-amber-800 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">♟</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">国际跳棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-amber-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('checkers')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-red-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">10×10</span>
                      <span className="bg-blue-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">吃子</span>
                      <span className="bg-purple-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">成王</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 黑白棋 */}
                <button
                  onClick={() => handleGameTypeSelect('reversi')}
                  className="group relative bg-gradient-to-br from-green-600 to-green-800 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">⚪</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">黑白棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-green-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('reversi')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-blue-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">8×8</span>
                      <span className="bg-purple-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">翻转</span>
                      <span className="bg-orange-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">占领</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 四子棋 */}
                <button
                  onClick={() => handleGameTypeSelect('connectfour')}
                  className="group relative bg-gradient-to-br from-orange-500 to-red-600 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">🔵</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">四子棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-orange-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('connectfour')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-orange-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">8×8</span>
                      <span className="bg-red-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">垂直</span>
                      <span className="bg-yellow-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">四连</span>
                      <span className="bg-amber-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">策略</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 中国象棋 */}
                <button
                  onClick={() => handleGameTypeSelect('xiangqi')}
                  className="group relative bg-gradient-to-br from-red-600 to-red-800 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">🐴</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">中国象棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-red-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('xiangqi')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-red-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">9×10</span>
                      <span className="bg-yellow-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">楚河汉界</span>
                      <span className="bg-purple-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">将帅</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 井字棋 */}
                <button
                  onClick={() => handleGameTypeSelect('tictactoe')}
                  className="group relative bg-gradient-to-br from-teal-500 to-cyan-600 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">⭕</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">井字棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-teal-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('tictactoe')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-teal-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">3×3</span>
                      <span className="bg-cyan-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">5×5</span>
                      <span className="bg-blue-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">7×7</span>
                      <span className="bg-purple-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">连线</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 斗兽棋 */}
                <button
                  onClick={() => handleGameTypeSelect('jungle')}
                  className="group relative bg-gradient-to-br from-orange-600 to-red-700 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">🐾</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">斗兽棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-orange-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('jungle')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-red-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">7×9</span>
                      <span className="bg-blue-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">动物</span>
                      <span className="bg-green-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">吃子</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 虎鸡虫棒棋 */}
                <button
                  onClick={() => handleGameTypeSelect('tigerchicken')}
                  className="group relative bg-gradient-to-br from-indigo-600 to-purple-700 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">🐅</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">虎鸡虫棒棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-indigo-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('tigerchicken')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-purple-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">7×7</span>
                      <span className="bg-blue-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">相克</span>
                      <span className="bg-green-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">大本营</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 直棋 */}
                <button
                  onClick={() => handleGameTypeSelect('straight')}
                  className="group relative bg-gradient-to-br from-cyan-500 to-blue-600 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">⚡</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">直棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-cyan-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('straight')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-cyan-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">24点</span>
                      <span className="bg-blue-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">成直</span>
                      <span className="bg-purple-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">吃子</span>
                      <span className="bg-green-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">传统</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 维京板棋 */}
                <button
                  onClick={() => handleGameTypeSelect('hnefatafl')}
                  className="group relative bg-gradient-to-br from-amber-600 to-amber-800 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">🛡️</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">维京板棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-amber-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('hnefatafl')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-red-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">11×11</span>
                      <span className="bg-blue-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">非对称</span>
                      <span className="bg-green-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">夹吃</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                {/* 萨米板棋 */}
                <button
                  onClick={() => handleGameTypeSelect('tablut')}
                  className="group relative bg-gradient-to-br from-rose-600 to-red-700 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">👑</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">萨米板棋</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-rose-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('tablut')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-rose-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">9×9</span>
                      <span className="bg-red-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">国王</span>
                      <span className="bg-orange-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">非对称</span>
                      <span className="bg-amber-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">北欧</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                  {/* 消消看 */}
                <button
                  onClick={() => handleGameTypeSelect('match3')}
                  className="group relative bg-gradient-to-br from-purple-500 to-pink-600 text-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 h-full min-h-[180px] sm:min-h-[200px] flex flex-col justify-between"
                >
                  <div className="flex flex-col h-full">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3">💎</div>
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-2 sm:mb-3">消消看</h3>
                    </div>
                    <div className="flex-1 flex items-center">
                      <p className="text-purple-100 text-xs sm:text-xs md:text-sm text-center line-clamp-2 sm:line-clamp-3 leading-relaxed">
                        {getGameTypeDescription('match3')}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-1 mt-2 sm:mt-3">
                      <span className="bg-purple-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">10×10</span>
                      <span className="bg-pink-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">消除</span>
                      <span className="bg-indigo-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">关卡</span>
                      <span className="bg-fuchsia-600 px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs">连击</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-xl transition-opacity"></div>
                </button>

                
              </div>
            </div>

            {/* 总体游戏特色 */}
            <div className="border-t pt-6">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">游戏合集特色</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 支持单人挑战，双人对战和人机对战模式</li>
                  <li>• 多种AI难度等级，适合不同水平玩家</li>
                  <li>• 精美的界面设计和流畅的游戏体验</li>
                  <li>• 完整的游戏功能：悔棋、重开、计分等</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <div className="text-sm text-gray-300">
                © 2025 <a href="https://oho.cn" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">OHO.cn</a> 版权所有
              </div>
              <div className="text-sm text-gray-300">
                <a href="http://beian.miit.gov.cn" target="_blank" rel="nofollow" className="hover:text-white transition-colors">浙ICP备13034790号-1</a>
              </div>
            </div>
            <div className="text-sm text-gray-400">
              益智棋类游戏合集 • 致敬经典策略游戏
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ModeSelect;